import { Button, Tooltip } from 'antd';
import clsx from 'clsx';
import { useMemo } from 'react';

import { CategoryAndSubCategory } from '../../../../../store/category';
import styles from './index.module.scss';

interface CategoryButtonProps {
  category: CategoryAndSubCategory;
  isActive: boolean;
  onClick: (id: string) => void;
  className?: string;
}

const LIMIT = 30;

const isAlphaNum = (ch: string) => /\p{L}|\p{N}/u.test(ch);

// Smart truncation with word-awareness
function truncateLabel(
  input: string,
  limit = LIMIT,
): { text: string; truncated: boolean } {
  if (!input) return { text: '', truncated: false };
  if (input.length <= limit) return { text: input, truncated: false };

  const slice = input.slice(0, limit);
  const prev = slice.charAt(slice.length - 1);
  const next = input.charAt(limit);

  // If we cut inside a word, backtrack to last space if possible
  const cutInsideWord = isAlphaNum(prev) && isAlphaNum(next);
  let base = slice.trimEnd();

  if (cutInsideWord) {
    const lastSpace = base.lastIndexOf(' ');

    if (lastSpace > 0) {
      base = base.slice(0, lastSpace);
    }
  }

  return { text: `${base}...`, truncated: true };
}

function CategoryButton({
  category: { id, name, file },
  isActive,
  onClick,
  className,
}: CategoryButtonProps) {
  const { text: label, truncated } = useMemo(
    () => truncateLabel(name ?? ''),
    [name],
  );

  return (
    <Tooltip title={truncated ? name : undefined}>
      <Button
        id={id}
        key={id}
        type={isActive ? 'primary' : 'default'}
        className={clsx(
          styles.categoryBtn,
          isActive ? styles.categoryBtnActive : '',
          className,
        )}
        onClick={() => onClick(id)}
      >
        {file?.location ? (
          <span className={styles.iconWrapper}>
            <img
              src={file.location}
              className={styles.icon}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                minWidth: 32,
                minHeight: 32,
              }}
              alt=""
              loading="lazy"
            />
          </span>
        ) : null}
        <span className={styles.name}>{label}</span>
      </Button>
    </Tooltip>
  );
}

export default CategoryButton;
